import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  ScrollView,
  Image,
  Modal,
  Platform,
} from 'react-native';
import { getArticle, createArticle } from '../services/articlesService';
import { deleteArticle } from '../services/articlesService';
import { useArticles } from '../contexts/ArticleContext';
import { selectAndUploadImage } from '../services/uploadService';
import { getStores } from '../services/storesService';
import { Ionicons } from '@expo/vector-icons';

const ArticleDetailScreen = ({ route, navigation }) => {
  const { articleId, searchQuery } = route.params || {};
  const isEditing = !!articleId;
  const { updateArticle, addArticle, removeArticle } = useArticles();

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(searchQuery || '');
  const [defaultUnit, setDefaultUnit] = useState('');
  const [defaultQuantity, setDefaultQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [newTagText, setNewTagText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);

  useEffect(() => {
    if (isEditing) {
      loadArticle();
    }
    loadStores();
  }, [articleId]);

  const loadStores = async () => {
    try {
      const result = await getStores();
      if (result.success) {
        setStores(result.data);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadArticle = async () => {
    const result = await getArticle(articleId);
    
    if (result.success) {
      const article = result.data;
      setName(article.name);
      setDefaultUnit(article.default_unit || '');
      setDefaultQuantity(Math.floor(article.default_quantity || 1).toString());
      setNotes(article.notes || '');
      setTags(article.default_tags || []);
      setImageUrl(article.image_url || '');
      setStoreId(article.last_used_store_id || null);
    } else {
      Alert.alert('Fehler', result.error);
    }
    
    setLoading(false);
  };

  const handleImageSelection = async (source) => {
    console.log('handleImageSelection called with source:', source);
    setUploadingImage(true);
    
    try {
      console.log('Calling selectAndUploadImage...');
      const result = await selectAndUploadImage(source);
      console.log('selectAndUploadImage result:', result);
      
      if (result.success) {
        setImageUrl(result.imageUrl);
        Alert.alert('Erfolg', 'Bild wurde erfolgreich hochgeladen');
      } else {
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      console.error('Image selection error:', error);
      Alert.alert('Fehler', 'Fehler beim Hochladen des Bildes: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const showImageOptions = () => {
    console.log('showImageOptions called');
    
    // Für Web: Direkt Galerie öffnen, da Kamera im Web nicht funktioniert
    if (typeof window !== 'undefined' && window.navigator) {
      console.log('Web environment detected, opening gallery directly');
      handleImageSelection('gallery');
      return;
    }
    
    Alert.alert(
      'Bild auswählen',
      'Woher möchtest du das Bild laden?',
      [
        { text: 'Abbrechen', style: 'cancel' },
        { text: 'Kamera', onPress: () => {
          console.log('Camera selected');
          handleImageSelection('camera');
        }},
        { text: 'Galerie', onPress: () => {
          console.log('Gallery selected');
          handleImageSelection('gallery');
        }},
      ]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert('Fehler', 'Bitte gib einen Namen ein');
      return;
    }

    const quantity = parseInt(defaultQuantity, 10);
    if (isNaN(quantity) || quantity <= 0) {
      Alert.alert('Fehler', 'Bitte gib eine gültige Standardmenge ein');
      return;
    }

    // Tags verarbeiten: Bestehende Tags + eventuell noch nicht gespeicherter Text
    let finalTags = [...tags];
    if (newTagText.trim()) {
      // Wenn noch Text im Eingabefeld steht, als Tag hinzufügen
      finalTags.push(newTagText.trim());
      setNewTagText(''); // Eingabefeld leeren
    }

    const articleData = {
      name: name.trim(),
      default_unit: defaultUnit.trim() || null,
      default_quantity: quantity,
      default_tags: finalTags,
      notes: notes.trim() || null,
      image_url: imageUrl.trim() || null,
      last_used_store_id: storeId,
    };

    console.log('🔍 ArticleDetailScreen: Saving with imageUrl:', imageUrl, 'processed:', imageUrl.trim() || null);

    setSaving(true);
    
    if (isEditing) {
      // Verwende den ArticleContext für Updates
      await updateArticle(articleId, articleData);
      navigation.goBack();
    } else {
      // Für neue Artikel: erst Backend, dann Context
      const result = await createArticle(articleData);
      if (result.success) {
        addArticle(result.data);
        navigation.goBack();
      } else {
        Alert.alert('Fehler', result.error);
      }
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    // Für Web: Browser-Confirm verwenden
    if (Platform.OS === 'web') {
      if (window.confirm('Möchten Sie diesen Artikel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
        try {
          const result = await deleteArticle(articleId);
          if (result.success) {
            removeArticle(articleId);
            navigation.goBack();
          } else {
            alert('Fehler: ' + result.error);
          }
        } catch (error) {
          alert('Fehler beim Löschen des Artikels');
        }
      }
    } else {
      Alert.alert(
        'Artikel löschen',
        'Möchten Sie diesen Artikel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
        [
          { text: 'Abbrechen', style: 'cancel' },
          {
            text: 'Löschen',
            style: 'destructive',
            onPress: async () => {
              try {
                const result = await deleteArticle(articleId);
                if (result.success) {
                  removeArticle(articleId);
                  navigation.goBack();
                } else {
                  Alert.alert('Fehler', result.error);
                }
              } catch (error) {
                Alert.alert('Fehler', 'Fehler beim Löschen des Artikels');
              }
            }
          }
        ]
      );
    }
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.formGroup}>
        <Text style={styles.label}>Name *</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          placeholder="z.B. Milch, Brot, Äpfel"
          autoFocus={!isEditing}
        />
      </View>

      <View style={styles.formGroup}>
        <Text style={styles.label}>Laden:</Text>
        <TouchableOpacity
          style={styles.storeSelectButton}
          onPress={() => setShowStoreModal(true)}
        >
          <Text style={styles.storeSelectText}>
            {storeId 
              ? stores.find(s => s.id === storeId)?.name || 'Laden wählen'
              : 'Kein Laden (Standard)'
            }
          </Text>
          <Ionicons name="chevron-down" size={20} color="#666" />
        </TouchableOpacity>
      </View>

      <View style={styles.formGroup}>
        <View style={styles.quantityLabelRow}>
          <Text style={styles.label}>Menge:</Text>
          <TextInput
            style={styles.unitInput}
            value={defaultUnit}
            onChangeText={setDefaultUnit}
            placeholder="Einheit (z.B. Stück, Liter, kg)"
          />
        </View>
        <View style={styles.quantityInputContainer}>
          {/* Mengeneingabe */}
          <TextInput
            style={styles.quantityInput}
            value={defaultQuantity}
            onChangeText={(text) => {
              // Erlaube beliebigen Text für Menge (z.B. "2-3", "ca. 500g", etc.)
              setDefaultQuantity(text);
            }}
            placeholder="z.B. 2, 2-3, ca. 500"
          />
          {/* Schnelleingabe-Buttons */}
          <View style={styles.quickInputButtons}>
            {[1, 2, 3, 4, 5, 6].map(number => (
              <TouchableOpacity
                key={number}
                style={styles.quickInputButton}
                onPress={() => setDefaultQuantity(number.toString())}
              >
                <Text style={styles.quickInputButtonText}>{number}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      <View style={styles.formGroup}>
        <Text style={styles.label}>Standard-Tags</Text>
        <View style={styles.tagsContainer}>
          {tags.map((tag, index) => (
            <View key={index} style={styles.tag}>
              <Text style={styles.tagText}>{tag}</Text>
              <TouchableOpacity
                onPress={() => {
                  const newTags = tags.filter((_, i) => i !== index);
                  setTags(newTags);
                }}
                style={styles.tagRemoveButton}
              >
                <Ionicons name="close" size={14} color="#2E7D32" />
              </TouchableOpacity>
            </View>
          ))}
          <TextInput
            style={styles.tagInputField}
            placeholder="Tag hinzufügen..."
            value={newTagText}
            onChangeText={(text) => {
              setNewTagText(text);
              if (text.includes(',') || text.includes(' ')) {
                const newTags = text.split(/[,\s]+/).map(tag => tag.trim()).filter(tag => tag.length > 0);
                setTags([...tags, ...newTags]);
                setNewTagText('');
              }
            }}
            onSubmitEditing={(event) => {
              const text = event.nativeEvent.text.trim();
              if (text.length > 0) {
                setTags([...tags, text]);
                setNewTagText('');
              }
            }}
            multiline={false}
            autoCapitalize="words"
            autoCorrect={false}
          />
        </View>
      </View>

      <View style={styles.formGroup}>
        <Text style={styles.label}>Notizen</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={notes}
          onChangeText={setNotes}
          placeholder="z.B. Nur Vollmilch, Möglichst frisch"
          multiline
          numberOfLines={4}
        />
      </View>

      {/* Button-Container: Alle Buttons in einer Zeile */}
      <View style={styles.buttonContainer}>
        {/* Löschen-Button nur beim Bearbeiten anzeigen */}
        {isEditing && (
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={handleDelete}
          >
            <Ionicons name="trash-outline" size={20} color="#fff" />
            <Text style={styles.deleteButtonText}>Löschen</Text>
          </TouchableOpacity>
        )}
        
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.cancelButtonText}>Abbrechen</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.saveButton}
          onPress={handleSave}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>
              {isEditing ? 'Speichern' : 'Erstellen'}
            </Text>
          )}
        </TouchableOpacity>
      </View>

      <View style={styles.formGroup}>
        <Text style={styles.label}>Produktbild</Text>
        
        {imageUrl && imageUrl.trim() !== '' ? (
          <View style={styles.imageContainer}>
            <Image source={{ uri: imageUrl }} style={styles.previewImage} />
            {/* Kleine Icon-Buttons über dem Bild */}
            <TouchableOpacity
              style={styles.editImageIcon}
              onPress={showImageOptions}
              disabled={uploadingImage}
            >
              <Ionicons name="create-outline" size={16} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.deleteImageIcon}
              onPress={() => {
                if (window.confirm('Möchtest du das Bild wirklich löschen?')) {
                  setImageUrl('');
                }
              }}
              disabled={uploadingImage}
            >
              <Ionicons name="trash-outline" size={16} color="#fff" />
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.imagePlaceholder}
            onPress={showImageOptions}
            disabled={uploadingImage}
          >
            {uploadingImage ? (
              <ActivityIndicator size="small" color="#2E7D32" />
            ) : (
              <>
                <Ionicons name="camera" size={32} color="#ccc" />
                <Text style={styles.placeholderText}>Bild hinzufügen</Text>
              </>
            )}
          </TouchableOpacity>
        )}
        
        <Text style={styles.hint}>Tippe auf das Feld, um ein Bild aus der Kamera oder Galerie auszuwählen</Text>
      </View>

      {/* Store-Auswahl-Modal */}
      <Modal
        visible={showStoreModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStoreModal(false)}
      >
        <View style={styles.storeModalOverlay}>
          <View style={styles.storeModalContent}>
            <Text style={styles.storeModalTitle}>Laden auswählen</Text>
            
            <ScrollView style={styles.storeList}>
              <TouchableOpacity
                style={[
                  styles.storeOption,
                  !storeId && styles.storeOptionSelected
                ]}
                onPress={() => {
                  setStoreId(null);
                  setShowStoreModal(false);
                }}
              >
                <Text style={styles.storeOptionText}>Kein Laden (Standard)</Text>
                {!storeId && (
                  <Ionicons name="checkmark" size={20} color="#2E7D32" />
                )}
              </TouchableOpacity>
              
              {stores.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[
                    styles.storeOption,
                    storeId === store.id && styles.storeOptionSelected
                  ]}
                  onPress={() => {
                    setStoreId(store.id);
                    setShowStoreModal(false);
                  }}
                >
                  <Text style={styles.storeOptionText}>{store.name}</Text>
                  {storeId === store.id && (
                    <Ionicons name="checkmark" size={20} color="#2E7D32" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: 20,
  },
  formGroup: {
    marginBottom: 20,
  },
  row: {
    flexDirection: 'row',
    gap: 15,
  },
  halfWidth: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 8,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
  },
  imageContainer: {
    alignItems: 'center',
    marginBottom: 10,
    position: 'relative',
  },
  previewImage: {
    width: 120,
    height: 120,
    borderRadius: 8,
    marginBottom: 10,
  },
  editImageIcon: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 0,
    borderBottomLeftRadius: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteImageIcon: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 0,
    borderBottomRightRadius: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholder: {
    width: 120,
    height: 120,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  placeholderText: {
    color: '#999',
    marginTop: 8,
    fontSize: 12,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    gap: 8,
  },
  saveButton: {
    backgroundColor: '#2E7D32',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  cancelButton: {
    backgroundColor: '#666',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: '#d32f2f',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  quantityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  unitInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: '#333',
    backgroundColor: '#f9f9f9',
  },
  quantityInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  quickInputButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  quickInputButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  quickInputButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  quantityInput: {
    width: 60,
    minWidth: 60,
    fontSize: 20,
    fontWeight: '700',
    color: '#2E7D32',
    textAlign: 'center',
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: '#2E7D32',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  tagText: {
    fontSize: 12,
    color: '#2E7D32',
    fontWeight: '600',
  },
  tagRemoveButton: {
    padding: 2,
  },
  tagInputField: {
    flex: 1,
    minWidth: 120,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  storeSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  storeSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  // Store Modal Styles
  storeModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  storeModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '80%',
    maxHeight: '60%',
    maxWidth: 400,
  },
  storeModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    padding: 20,
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  storeList: {
    maxHeight: 300,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  storeOptionSelected: {
    backgroundColor: '#f0f8f0',
  },
  storeOptionText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
});

export default ArticleDetailScreen;